import { t } from "@lingui/core/macro";
import type { CSSProperties, PointerEvent, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useBotsSidebarWidth } from "./shell/sidebar-layout";

const STORAGE_KEY = "clvabot:right-panel-width:v2";
const MIN_WIDTH = 320;
const ABSOLUTE_MAX = 1000;
const MIN_CHAT_WIDTH = 320;
// Prefer enough room for a message card's icon, label and action before shrinking the chat.
const PREFERRED_CHAT_WIDTH = 520;
/** The 4px sidebar divider plus the 6px inset before the chat panel. */
const SHELL_CHROME_WIDTH = 10;

function maximumWidth(reservedLeadingPx: number, viewportWidth = window.innerWidth) {
  return Math.max(
    MIN_WIDTH,
    Math.min(
      ABSOLUTE_MAX,
      viewportWidth - reservedLeadingPx - SHELL_CHROME_WIDTH - PREFERRED_CHAT_WIDTH,
    ),
  );
}

/** No saved width means "as wide as the chat can spare"; a drag replaces it with a fixed width. */
function readPreferredWidth(): number | null {
  try {
    const saved = Number(localStorage.getItem(STORAGE_KEY));
    if (saved >= MIN_WIDTH) return Math.min(ABSOLUTE_MAX, saved);
  } catch {
    /* Storage may be disabled. */
  }
  return null;
}

export function ResizableSidePanel({
  open,
  panel,
  children,
}: {
  open: boolean;
  panel: string;
  children: ReactNode;
}) {
  const reservedLeadingPx = useBotsSidebarWidth();
  const [preferredWidth, setPreferredWidth] = useState<number | null>(readPreferredWidth);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const maxWidth = maximumWidth(reservedLeadingPx, viewportWidth);
  const overlay = viewportWidth - reservedLeadingPx < MIN_CHAT_WIDTH + MIN_WIDTH;
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ x: number; width: number; direction: number } | null>(null);
  const width =
    preferredWidth === null ? maxWidth : Math.max(MIN_WIDTH, Math.min(maxWidth, preferredWidth));

  useEffect(() => {
    if (preferredWidth === null) return;
    try {
      localStorage.setItem(STORAGE_KEY, String(preferredWidth));
    } catch {
      /* Storage may be disabled. */
    }
  }, [preferredWidth]);

  useEffect(() => {
    function syncMax() {
      setViewportWidth(window.innerWidth);
    }
    syncMax();
    window.addEventListener("resize", syncMax);
    return () => window.removeEventListener("resize", syncMax);
  }, []);

  function stopDrag(event: PointerEvent<HTMLHRElement>) {
    drag.current = null;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
  }

  function setWidthFromUser(next: number) {
    setPreferredWidth(Math.max(MIN_WIDTH, Math.min(maximumWidth(reservedLeadingPx), next)));
  }

  return (
    <div
      data-overlay={overlay || undefined}
      style={{ "--panel-width": `${width}px` } as CSSProperties}
      className={`absolute inset-y-0 end-0 z-20 flex min-h-0 shrink-0 flex-col ${overlay ? "md:absolute" : "md:relative md:my-1.5 md:me-1.5"} ${dragging ? "" : "transition-[width] duration-150 ease-out"} ${open ? "w-full max-w-[384px] md:w-(--panel-width) md:max-w-none" : "pointer-events-none w-0"}`}
    >
      <aside
        data-testid="side-panel"
        data-panel={panel}
        className={`flex min-h-0 flex-1 flex-col overflow-hidden bg-panel ${overlay ? "" : "md:rounded-md md:border md:border-border"} ${open ? "border-s border-sidebar-border" : ""}`}
      >
        {children}
      </aside>
      {open ? (
        <hr
          tabIndex={0}
          aria-label={t`Resize panel`}
          aria-orientation="vertical"
          aria-valuemin={MIN_WIDTH}
          aria-valuemax={maxWidth}
          aria-valuenow={Math.round(width)}
          className={`absolute inset-y-0 -start-[5px] z-30 m-0 hidden h-full w-1 touch-none cursor-col-resize border-0 bg-transparent outline-none after:pointer-events-none after:absolute after:inset-y-1.5 after:start-1/2 after:w-0.5 after:-translate-x-1/2 after:rounded-full after:bg-resize-handle after:opacity-0 after:transition-opacity after:content-[''] hover:after:opacity-100 focus-visible:outline-none focus-visible:ring-0 focus-visible:after:opacity-100 ${dragging ? "after:opacity-100" : ""} ${overlay ? "" : "md:block"}`}
          onPointerDown={(event) => {
            if (event.button !== 0) return;
            event.preventDefault();
            event.currentTarget.setPointerCapture(event.pointerId);
            drag.current = {
              x: event.clientX,
              width,
              direction: document.documentElement.dir === "rtl" ? -1 : 1,
            };
            setDragging(true);
          }}
          onPointerMove={(event) => {
            if (drag.current)
              setWidthFromUser(
                drag.current.width + (drag.current.x - event.clientX) * drag.current.direction,
              );
          }}
          onPointerUp={stopDrag}
          onPointerCancel={stopDrag}
          onLostPointerCapture={() => {
            drag.current = null;
            setDragging(false);
          }}
          onKeyDown={(event) => {
            const direction = document.documentElement.dir === "rtl" ? -1 : 1;
            if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
              event.preventDefault();
              setWidthFromUser(width + (event.key === "ArrowLeft" ? 32 : -32) * direction);
            } else if (event.key === "Home") {
              event.preventDefault();
              setWidthFromUser(MIN_WIDTH);
            } else if (event.key === "End") {
              event.preventDefault();
              setWidthFromUser(maximumWidth(reservedLeadingPx));
            }
          }}
        />
      ) : null}
    </div>
  );
}
